import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { formatCompactCurrencyNumber } from '../../../lib/utils'
import { localeForCurrency } from '../../../utils/currencyLocale'
import type { ArrearsBacklogBar } from '../arrearsBacklogDrill'
import type { RechartsClickData } from '../types'

/** CR-8236: one bar, its due-date window included, handed back on click for its drill. */
type ArrearsBacklogData = ArrearsBacklogBar

interface ArrearsBacklogChartProps {
  data?: ArrearsBacklogData[]
  onBarClick?: (data: ArrearsBacklogData) => void
  showValue?: boolean // Whether to show value or count
  currency?: string
}

/** One series, one colour. Bars were once coloured by their share of the tallest bar,
 *  which read as a severity judgement and reshuffled with every filter. The legend in
 *  DefaultTab names the series in this same colour. */
export const ARREARS_BACKLOG_COLOR = '#3b82f6'

/** CR-8660: weeks run oldest first, 12+ down to 1, whatever order the payload holds them in.
 *  A label that is not a week sorts last rather than leaving the order to chance. */
const weeksOverdue = (bar: ArrearsBacklogData): number => parseInt(bar.week, 10) || 0

export function ArrearsBacklogChart({
  data,
  onBarClick,
  showValue = true,
  currency = 'GBP',
}: ArrearsBacklogChartProps) {
  const chartData = [...(data || [])].sort((a, b) => weeksOverdue(b) - weeksOverdue(a))
  const dataKey = showValue ? 'value' : 'count'

  const formatYAxis = (value: number) => {
    if (showValue) {
      // Same "3.4Cr" / "3.1M" shape as every other money axis on the
      // dashboard — CR-7120: numbers only, no currency symbol on an axis.
      return formatCompactCurrencyNumber(value, currency)
    }
    return value.toString()
  }

  const formatTooltipValue = (value: number) => {
    if (showValue) {
      return new Intl.NumberFormat(localeForCurrency(currency), {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value).replace('US$', '$')
    }
    return `${value} Arrears Lines`
  }

  // CR-8601: a click anywhere in the hovered (highlighted) column opens that
  // week, not only a click on the bar itself. Recharts 3 reports a chart click
  // as activeLabel / activeIndex, so the week is resolved back to its bar; a
  // click with no column under it opens nothing.
  const handleChartClick = (state: RechartsClickData | null | undefined) => {
    if (!onBarClick || !state) return
    const label = state.activeLabel
    const index = Number(state['activeIndex'])
    const bar = label !== undefined
      ? chartData.find((d) => d.week === String(label))
      : Number.isInteger(index) ? chartData[index] : undefined
    if (bar) onBarClick(bar)
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={chartData}
        onClick={onBarClick ? (state) => handleChartClick(state as RechartsClickData) : undefined}
        style={{ cursor: onBarClick ? 'pointer' : 'default' }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis
          dataKey="week"
          tick={{ fontSize: 11 }}
          label={{ value: 'Weeks Overdue', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#9ca3af' }}
        />
        <YAxis
          tick={{ fontSize: 11 }}
          tickFormatter={formatYAxis}
        />
        <Tooltip
          formatter={(value: number) => [formatTooltipValue(value), showValue ? 'Outstanding Value' : 'Arrears Lines']}
          cursor={onBarClick ? { fill: 'rgba(59, 130, 246, 0.15)', stroke: '#3b82f6', strokeWidth: 1 } : { fill: 'rgba(59, 130, 246, 0.1)' }}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length > 0) {
              return (
                <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
                  <p className="font-medium mb-1">Week {label}</p>
                  <p className="text-gray-600">{formatTooltipValue(payload[0].value as number)}</p>
                  {onBarClick && (
                    <p className="text-xs text-blue-600 mt-2 font-medium">Click to view details →</p>
                  )}
                </div>
              )
            }
            return null
          }}
        />
        <Bar
          dataKey={dataKey}
          name={showValue ? 'Outstanding Value' : 'Arrears Lines'}
          fill={ARREARS_BACKLOG_COLOR}
          radius={[4, 4, 0, 0]}
          cursor={onBarClick ? 'pointer' : undefined}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

export default ArrearsBacklogChart
